import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useCallback, useEffect, useRef, useState } from 'react';
import { updateVesselSchedule } from '../services/ScheduleRepository';
import { StorageKey } from '../../../shared/auth/DataService';
import { useUserPreferences } from '../../../shared/contexts/UserPreferencesContext';
import { cwDataGridSx } from '../../../shared/utils/calendarWeekStyling';
import { tileColorSx } from '../../../shared/utils/dataGridTileStyling';
import columns from './schedule/columns/columns';
import columnsDefinitions from './schedule/columns/columnsDefinitions';
import DialogComponentHistory from './schedule/components/DialogComponentHistory';
import stockColumnsDefinitions from './schedule/columns/stockColumnsDefinitions';
import useFetchData from '../hooks/useFetchData';
import GroupEditModal from './sales/modals/VoyageGroupEditModal';
import ColumnPrefsToolbar from './sales/components/shared/ColumnPrefsToolbar';
import useColumnPreferences from '../../../shared/hooks/useColumnPreferences';
import { useVesselScheduleData } from '../hooks/useVesselScheduleData';

dayjs.extend(isoWeek);

const VesselScheduleHomePage = () => {
  const theme = useTheme();
  const { preferences } = useUserPreferences();
  const userDateFormat = preferences.dateFormat || 'DD.MM.YYYY';
  const [isVesselModalOpen, setIsVesselModalOpen] = useState(false);
  const [vesselFormMode, setVesselFormMode] = useState('create');
  const [editVoyageRow, setEditVoyageRow] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { rows, fetchData } = useVesselScheduleData();
  const { shipmentDetails } = useFetchData(undefined, {
    includeShipments: true,
  });
  const { stock } = useFetchData();

  // Column preferences for Vessel Table
  const vesselColPrefs = useColumnPreferences({
    hiddenKey: 'hiddenVesselJournalVesselColumns',
    orderKey: 'vesselJournalVesselColumnOrder',
    widthsKey: 'vesselJournalVesselColumnWidths',
    pinnedKey: 'vesselJournalVesselPinnedColumns',
    defaultPinned: {
      left: ['calendarWeek', 'initialETDFormatted'],
      right: ['actions'],
    },
  });

  // Column preferences for Stock Table
  const stockColPrefs = useColumnPreferences({
    hiddenKey: 'hiddenVesselJournalStockColumns',
    orderKey: 'vesselJournalStockColumnOrder',
    widthsKey: 'vesselJournalStockColumnWidths',
    pinnedKey: 'vesselJournalStockPinnedColumns',
    defaultPinned: { left: ['date'] },
  });

  const [vesselHighlightedRowId, setVesselHighlightedRowId] = useState(null);
  const [stockHighlightedRowId, setStockHighlightedRowId] = useState(null);
  const [isVesselFullScreen, setIsVesselFullScreen] = useState(false);
  const [isStockFullScreen, setIsStockFullScreen] = useState(false);
  const vesselHasAutoScrolled = useRef(false);
  const stockHasAutoScrolled = useRef(false);

  const onVesselModalOpen = () => {
    setIsVesselModalOpen(true);
  };

  /** Assigns week-even / week-odd class based on the stock row's date */
  const getStockRowClassName = useCallback(
    (params) => {
      const date = params.row.date;
      if (date == null) return '';
      const d = dayjs(date);
      const classes = [];
      if (d.isValid())
        classes.push(d.isoWeek() % 2 === 0 ? 'week-even' : 'week-odd');
      if (params.id === stockHighlightedRowId) classes.push('today-highlight');
      return classes.join(' ');
    },
    [stockHighlightedRowId]
  );

  /** Assigns week-even / week-odd + today highlight for vessel rows */
  const getVesselRowClassName = useCallback(
    (params) => {
      const date = params.row.actualETD ?? params.row.initialETD;
      if (date == null) return '';
      const d = dayjs(date);
      const classes = [];
      if (d.isValid())
        classes.push(d.isoWeek() % 2 === 0 ? 'week-even' : 'week-odd');
      if (params.id === vesselHighlightedRowId) classes.push('today-highlight');
      return classes.join(' ');
    },
    [vesselHighlightedRowId]
  );

  /** Scroll the vessel grid to today's row or nearest future date */
  const scrollVesselToToday = useCallback(() => {
    const today = dayjs().startOf('day');
    const api = vesselColPrefs.apiRef.current;
    if (!api?.getSortedRowIds) return;
    const sortedIds = api.getSortedRowIds();
    let targetId = null;

    for (const id of sortedIds) {
      const row = api.getRow(id);
      if (!row) continue;
      const d = dayjs(row.actualETD ?? row.initialETD);
      if (!d.isValid()) continue;
      if (d.startOf('day').isSame(today) || d.startOf('day').isAfter(today)) {
        targetId = id;
        break;
      }
    }
    // Fallback to last row if all dates are in the past
    if (targetId == null && sortedIds.length > 0)
      targetId = sortedIds[sortedIds.length - 1];
    if (targetId == null) return;

    setVesselHighlightedRowId(targetId);
    const visualIndex = sortedIds.indexOf(targetId);
    if (visualIndex !== -1) {
      setTimeout(() => {
        const rowHeight = api.state.dimensions?.rowHeight ?? 52;
        api.scroll({ top: visualIndex * rowHeight });
      }, 0);
    }
  }, [vesselColPrefs.apiRef]);

  /** Scroll the stock grid to today's row */
  const scrollStockToToday = useCallback(() => {
    const today = dayjs().startOf('day');
    const api = stockColPrefs.apiRef.current;
    if (!api?.getSortedRowIds) return;
    const sortedIds = api.getSortedRowIds();
    let targetId = null;

    for (const id of sortedIds) {
      const row = api.getRow(id);
      if (!row) continue;
      const d = dayjs(row.date);
      if (!d.isValid()) continue;
      if (d.startOf('day').isSame(today) || d.startOf('day').isAfter(today)) {
        targetId = id;
        break;
      }
    }
    if (targetId == null && sortedIds.length > 0)
      targetId = sortedIds[sortedIds.length - 1];
    if (targetId == null) return;

    setStockHighlightedRowId(targetId);
    const visualIndex = sortedIds.indexOf(targetId);
    if (visualIndex !== -1) {
      setTimeout(() => {
        const rowHeight = api.state.dimensions?.rowHeight ?? 52;
        api.scroll({ top: visualIndex * rowHeight });
      }, 0);
    }
  }, [stockColPrefs.apiRef]);

  // Auto-scroll to today when data first loads
  useEffect(() => {
    if (!vesselHasAutoScrolled.current && rows.length > 0) {
      vesselHasAutoScrolled.current = true;
      setTimeout(() => scrollVesselToToday(), 0);
    }
  }, [rows, scrollVesselToToday]);

  useEffect(() => {
    if (!stockHasAutoScrolled.current && stock.length > 0) {
      stockHasAutoScrolled.current = true;
      setTimeout(() => scrollStockToToday(), 0);
    }
  }, [stock, scrollStockToToday]);

  const onVesselModalClose = useCallback(() => {
    setIsVesselModalOpen(false);
    setVesselFormMode('create');
    setEditVoyageRow(null);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHistoryClick = (row) => {
    setSelectedRow(row);
    setHistoryOpen(true);
  };

  const handleHistoryClose = () => {
    setHistoryOpen(false);
    setSelectedRow(null);
  };

  const handleEditClick = (row) => {
    setEditVoyageRow(row);
    setVesselFormMode('group-edit');
    setIsVesselModalOpen(true);
  };

  const handleCancelClick = async (row) => {
    if (row) {
      const emea = localStorage.getItem(StorageKey.userInfo_sub);
      const vesselDel = {
        imo: row.imo,
        vessel: row.vessel,
        departurePort: row.departurePort.id,
        departureCountry: row.departureCountry.countryCode,
        portOperator: row.portOperator,
        destinationPort: row.destinationPort.id,
        destinationCountry: row.destinationCountry.countryCode,
        shippingCompany: row.shippingCompany,
        initialETD: dayjs(row.initialETD),
        firstPOVolumeRequest: row.firstPOVolumeRequest,
        firstShippingLineOffer: row.firstShippingLineOffer,
        adjustedPORequest: row.adjustedPORequest,
        finalAgreement: row.finalAgreement,
        stock: row.stock,
        effectiveShipped: row.effectiveShipped,
        initialETA: dayjs(row.initialETA),
        slotConfirmed: row.slotConfirmed,
        generalRemarks: row.generalRemarks,
        version: row.version,
        lastUpdatedBy: { emea: emea },
        actualETA: dayjs(row.actualETA),
        actualETD: dayjs(row.actualETD),
        cancelled: true,
      };
      try {
        const response = await updateVesselSchedule(row.id, vesselDel);
        if (response.status === 200) {
          alert('Vessel schedule updated successfully');
          onVesselModalClose();
        } else {
          alert('Failed to update vessel schedule');
        }
      } catch (error) {
        console.error('Error updating vessel schedule:', error);
        alert('Error updating vessel schedule');
      }
    }
  };

  return (
    <>
      <GroupEditModal
        open={isVesselModalOpen}
        onClose={onVesselModalClose}
        mode={vesselFormMode}
        voyageRow={vesselFormMode === 'group-edit' ? editVoyageRow : null}
      />

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '25vh',
          marginBottom: '20px',
        }}
      >
        <img
          src='/resources/amg_gt.jpg'
          alt=''
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div
          style={{
            position: 'absolute',
            top: '20%',
            left: '8%',
            textAlign: 'left',
            color: '#FFFFFF',
          }}
        >
          <Typography variant={'h1'}>Vessel Journal</Typography>
        </div>
      </div>

      <Box sx={{ marginBottom: '5px' }}>
        <Button variant='outlined' onClick={onVesselModalOpen}>
          Add Ship
        </Button>
      </Box>

      <Box
        sx={{
          height: '60vh',
          width: '100%',
          '& .today-highlight': {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        <DataGridPro
          {...vesselColPrefs.getColumnPrefsDataGridProps()}
          rows={rows}
          columns={vesselColPrefs.applyColumnWidths(
            vesselColPrefs.applyColumnOrder([
              {
                field: 'calendarWeek',
                headerName: 'CW',
                description: 'Calendar Week',
                width: 80,
                type: 'number',
              },
              ...columns(
                handleHistoryClick,
                handleEditClick,
                handleCancelClick,
                theme
              ),
            ])
          )}
          initialState={{
            sorting: {
              sortModel: [{ field: 'actualETDFormatted', sort: 'asc' }],
            },
            columns: { columnVisibilityModel: { id: false } },
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: ColumnPrefsToolbar }}
          slotProps={{
            toolbar: {
              onSave: vesselColPrefs.handleSaveColumns,
              fullScreen: isVesselFullScreen,
              onToggleFullScreen: () => setIsVesselFullScreen((p) => !p),
              onScrollToToday: scrollVesselToToday,
            },
          }}
          sx={{ ...cwDataGridSx, ...tileColorSx }}
          getRowClassName={getVesselRowClassName}
        />
      </Box>

      <Typography variant='h2' marginTop={10}>
        Shipment Table
      </Typography>

      <Box sx={{ height: '60vh', width: '100%', marginTop: '20px' }}>
        <DataGridPro
          rows={shipmentDetails}
          columns={columnsDefinitions({ dateFormat: userDateFormat })}
          getRowId={(row) => row.shipmentId}
          initialState={{
            sorting: {
              sortModel: [{ field: 'actualEtd', sort: 'asc' }],
            },
            pinnedColumns: {
              left: ['departurePort'],
              right: ['generalRemarks'],
            },
          }}
          sx={tileColorSx}
        />
      </Box>
      <Typography variant='h2' marginTop={10}>
        Stock Table
      </Typography>
      <Box
        sx={{
          height: '60vh',
          width: '100%',
          marginTop: '20px',
          '& .today-highlight': {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        <DataGridPro
          {...stockColPrefs.getColumnPrefsDataGridProps()}
          rows={stock}
          columns={stockColPrefs.applyColumnWidths(
            stockColPrefs.applyColumnOrder(
              stockColumnsDefinitions({ dateFormat: userDateFormat })
            )
          )}
          getRowId={(row) => row.id}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'asc' }],
            },
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: ColumnPrefsToolbar }}
          slotProps={{
            toolbar: {
              onSave: stockColPrefs.handleSaveColumns,
              fullScreen: isStockFullScreen,
              onToggleFullScreen: () => setIsStockFullScreen((p) => !p),
              onScrollToToday: scrollStockToToday,
            },
          }}
          sx={cwDataGridSx}
          getRowClassName={getStockRowClassName}
        />
      </Box>

      <DialogComponentHistory
        open={historyOpen}
        handleClose={handleHistoryClose}
        row={selectedRow}
      />
    </>
  );
};

VesselScheduleHomePage.propTypes = {};

VesselScheduleHomePage.defaultProps = {};

export { VesselScheduleHomePage };
